/*
  Behaviour for the House of brands slider — the three things the UI kit drives
  from JS and CSS alone cannot. Everything else about the card (rings drawing in,
  wordmark fading out, can lid scaling up) is pure CSS in snusify-brands.css and
  needs nothing here.

  Constants are the kit's own: BrandSlider()/BrandCard() in the bundle.

  Loaded by sections/snusify-brands.liquid.
*/

/* Phones have no hover, so the same animation is driven by visibility instead.
   Only a card that is essentially fully visible lights up — a half-scrolled card
   stays at rest — and it settles back to rest when it leaves, so swiping to the
   next card retracts the previous one. .98 rather than 1 because subpixel layout
   means a fully visible element rarely reports an exact 1. */
const SNUSIFY_REVEAL_FULL = 0.98;
const SNUSIFY_REVEAL_IN = 300;

/* IntersectionObserver clips against scrolling ancestors, so a card swiped
   halfway out of the horizontal track reports ~.5 and is left dark. */
const SNUSIFY_REVEAL_STEPS = [0, 0.25, 0.5, 0.75, SNUSIFY_REVEAL_FULL, 1];

/* How long the card animates before a tap is allowed to navigate. */
const SNUSIFY_PREROLL = 820;

class SnusifyBrandCards extends BaseElement {
  connectedCallback() {
    super.connectedCallback();

    this.track = this.querySelector('.snusify-slider');
    this.timer = null;

    if (this.track) this.initDrag();
    this.initReveal();
    this.on(this, 'click', this.onClick.bind(this));

    this.registerCleanup(() => clearTimeout(this.timer));
  }

  get cards() {
    return this.querySelectorAll('.snusify-brandcard');
  }

  /* The kit's own gate: hover is kept on desktop, and reduced motion keeps the
     resting state everywhere. 820px is the kit's breakpoint, not Concept's. */
  get revealMql() {
    return (this._revealMql ??= matchMedia('(max-width: 820px)'));
  }

  get motionReduced() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /*
    Below 820px each card lights itself when it is (essentially) fully in view.
    React's effect re-ran on mount only; here the media query is watched too, so
    resizing across the breakpoint does not leave the cards permanently dark or
    permanently observed.
  */
  initReveal() {
    const sync = () => {
      this.teardownReveal();
      if (!this.revealMql.matches || this.motionReduced) return;

      const timers = new WeakMap();
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const card = entry.target;
            // A card that leaves mid-wait never lights.
            clearTimeout(timers.get(card));

            if (entry.intersectionRatio >= SNUSIFY_REVEAL_FULL) {
              timers.set(
                card,
                setTimeout(() => card.classList.add('is-activating'), SNUSIFY_REVEAL_IN)
              );
            } else {
              // CSS handles the retract.
              card.classList.remove('is-activating');
            }
          }
        },
        { threshold: SNUSIFY_REVEAL_STEPS }
      );

      for (const card of this.cards) observer.observe(card);

      this.revealTeardown = () => {
        observer.disconnect();
        for (const card of this.cards) {
          clearTimeout(timers.get(card));
          card.classList.remove('is-activating');
        }
      };
    };

    sync();
    this.on(this.revealMql, 'change', sync);
    this.registerCleanup(() => this.teardownReveal());
  }

  teardownReveal() {
    this.revealTeardown?.();
    this.revealTeardown = null;
  }

  /*
    Mouse click-and-drag — the one gesture native overflow does not give a mouse
    user. Touch and trackpad already scroll on their own, so only a primary mouse
    button starts a drag.
  */
  initDrag() {
    const track = this.track;
    let id = null;
    let startX = 0;
    let startLeft = 0;
    let moved = false;

    this.on(track, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      id = event.pointerId;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      moved = false;
      track.classList.add('is-dragging');

      try {
        track.setPointerCapture(id);
      } catch (error) {
        /* capture is best-effort; the drag still tracks without it */
      }
    });

    this.on(track, 'pointermove', (event) => {
      if (id === null || event.pointerId !== id) return;

      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      track.scrollLeft = startLeft - delta;
    });

    const end = (event) => {
      if (id === null || event.pointerId !== id) return;

      try {
        track.releasePointerCapture(id);
      } catch (error) {
        /* already released */
      }

      id = null;
      track.classList.remove('is-dragging');

      // Swallow the click that ends a real drag, so dragging past a card does
      // not follow its link.
      if (moved) {
        const kill = (clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
        };
        track.addEventListener('click', kill, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', kill, true), 0);
      }
    };

    this.on(track, 'pointerup', end);
    this.on(track, 'pointercancel', end);
  }

  /* True where the pointer cannot hover — the one case the preroll exists for. */
  get noHover() {
    return matchMedia('(hover: none)').matches;
  }

  /*
    The kit holds the card in its activated state for PREROLL before changing
    view, so the animation is seen on a device that never had a hover to show it.
    Here the destination is a real page load rather than a client-side route, so
    the delay sits on top of the navigation itself.

    Gated on that stated reason rather than run everywhere. With a hover-capable
    pointer the animation has already played under the cursor, so the preroll is
    820ms of nothing before a navigation that already costs ~1.5s. This is a
    deliberate divergence from the port, which keeps it unconditionally for a
    1:1 match with the kit; drop the `noHover` guard to restore that.
  */
  onClick(event) {
    const card = event.target.closest('.snusify-brandcard');
    if (!card || !this.contains(card) || !card.href) return;

    // Hover already showed it: go straight there.
    if (!this.noHover) return;

    // Leave modified clicks (new tab, new window, download) entirely alone.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    // No motion wanted: go straight there.
    if (this.motionReduced) return;

    // Ignore repeat taps mid-preroll.
    if (this.timer) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    card.classList.add('is-activating');

    const href = card.href;
    this.timer = setTimeout(() => {
      this.timer = null;
      location.href = href;
    }, SNUSIFY_PREROLL);
  }
}
customElements.define('snusify-brand-cards', SnusifyBrandCards);
